import { InfoCircleOutlined } from '@ant-design/icons'
import { useBoolean, useRequest, useUpdateEffect } from 'ahooks'
import { Button, Popconfirm, Tooltip } from 'antd'
import { isNil } from 'lodash'

import { queryAuditEnterpriseDetailByMemberIdApi } from '@eastcoal/api/auth/audit/queryAuditEnterpriseDetailByMemberId'
import { CertificationEntity } from '@eastcoal/api/auth/interface'

import { ProTable } from '@eastcoal/common/components/ProTable'
import { UploadFile } from '@eastcoal/common/components/UploadFile'
import { pick } from '@eastcoal/common/utils/object'

import { CERTIFICATION_DATE_STATUS } from '@eastcoal/shared/components/CertificationManage/utils/enum'
import { isValidCertificationDate } from '@eastcoal/shared/components/CertificationManage/utils/isValidDate'
import { MemberType } from '@eastcoal/shared/enums/MemberType'

import { useAuctionListStore } from '../../../../../Store/hooks/useStore'

import { ApplyCertificationsCheckButtonProps } from './interface'

export const ApplyCertificationsCheckButton = ({
  record: { memberId, memberType }
}: ApplyCertificationsCheckButtonProps) => {
  const { bidMemberListModalVisible } = useAuctionListStore(state =>
    pick(state, ['bidMemberListModalVisible'])
  )

  const [open, openAction] = useBoolean(false)

  useUpdateEffect(() => {
    if (!bidMemberListModalVisible) {
      openAction.setFalse()
    }
  }, [bidMemberListModalVisible])

  const { data, loading } = useRequest(
    () => queryAuditEnterpriseDetailByMemberIdApi({ memberId }),
    {
      manual: false,
      ready: !isNil(memberId) && open,
      refreshDeps: [memberId]
    }
  )

  if (memberType === MemberType['PERSON']) {
    return null
  }

  const renderContent = () => {
    return (
      <ProTable
        loading={loading}
        pagination={false}
        rowKey='image'
        columns={[
          { title: '资质名称', dataIndex: 'name' },
          {
            title: '有效期限',
            dataIndex: 'timeType',
            render: (_, record, index) => {
              const { endTime, beginTime, timeType } = record
              const dateStatus = isValidCertificationDate(record)
              const desc =
                dateStatus === CERTIFICATION_DATE_STATUS.EFFECTIVE
                  ? '未过期'
                  : dateStatus === CERTIFICATION_DATE_STATUS.OUTEFFECTIVE
                    ? '已过期'
                    : '未生效'
              if (index > 1 && timeType) {
                return '无期限'
              }
              if (!timeType) {
                return (
                  <Tooltip title={`${beginTime} ~ ${endTime}`}>
                    <span>有期限</span>(
                    <span style={{ color: 'orange' }}>{desc}</span>) &nbsp;
                    <InfoCircleOutlined />
                  </Tooltip>
                )
              }
              return null
            }
          },
          {
            title: '资质附件',
            dataIndex: 'image',
            render: value => {
              return (
                <UploadFile
                  readPretty
                  value={[value]}
                  showUploadList={{
                    showDownloadIcon: true,
                    showPreviewIcon: true,
                    showRemoveIcon: false
                  }}
                />
              )
            }
          }
        ]}
        dataSource={
          isNil(data)
            ? []
            : [
                {
                  image: data.licenseOssId ?? '',
                  name: '营业执照',
                  timeType: 1
                } as CertificationEntity
              ].concat(data.certifications ?? [])
        }
      />
    )
  }

  return (
    <Popconfirm
      cancelButtonProps={{ hidden: true }}
      description={renderContent}
      open={open}
      placement='leftBottom'
      title='资质信息'
      trigger='click'
      onConfirm={openAction.setFalse}
    >
      <Button type='link' onClick={openAction.setTrue}>
        查看资质
      </Button>
    </Popconfirm>
  )
}
